import { useMemo, useState, type ReactNode } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type { GridColDef, GridRowId, GridValidRowModel } from '@mui/x-data-grid';
import type { FilterDef } from '@/components/FilterBar';
import { DataTable } from './DataTable';

/** Label / value pairs above a table — usually the totals of what it lists. */
export function TotalsStrip({ items }: { items: [string, ReactNode][] }) {
  return (
    <Stack direction="row" spacing={3} sx={{ pb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
      {items.map(([label, value]) => (
        <Box key={label}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

/** A dropdown filter and the rows it keeps for a chosen value. */
export type LocalFilter<R> = FilterDef & { keep: (row: R, value: string) => boolean };

const NO_FILTERS: never[] = [];

type Props<R extends GridValidRowModel> = {
  rows: R[];
  columns: GridColDef<R>[];
  getRowId: (row: R) => GridRowId;
  /** What the search box matches a row against, ignoring case. */
  searchText: (row: R) => string;
  searchPlaceholder: string;
  /** What a row is, for the count beside the search: ['employee', 'employees']. */
  noun: [string, string];
  filters?: LocalFilter<R>[];
  /** Figures for the rows the search and filters leave, shown above the table. */
  summary?: (shown: R[]) => [string, ReactNode][];
  emptyMessage?: ReactNode;
  /** Controls beside the search, e.g. a switch. */
  toolbarEnd?: ReactNode;
  pin?: { storageKey: string; defaultPinned?: string[] };
  onRowClick?: (row: R) => void;
  /** The row to show as selected. */
  selectedId?: GridRowId | null;
  pageSize?: number;
  loading?: boolean;
  /** Rows grow to fit long text instead of cutting it off. */
  wrap?: boolean;
};

// Cell classes a column can name: `cellClassName: 'mono'` for codes and
// PANs, 'strong' for a total. Exported for DataTables built directly.
export const CELL_SX = {
  '& .mono': { fontFamily: 'ui-monospace, monospace' },
  '& .strong': { fontWeight: 600 },
};
const WRAP_SX = { '& .MuiDataGrid-cell': { py: 1, whiteSpace: 'normal', lineHeight: 1.45 } };
// A selected row marks the record open beside the table, not a pick for a bulk
// action, so "1 row selected" in the footer would only confuse.
const NO_SELECTED_COUNT_SX = { '& .MuiDataGrid-selectedRowCount': { visibility: 'hidden' } };

/**
 * DataTable for rows a page already holds. Search and filters narrow them,
 * the grid sorts and pages them, and only the rows on screen are drawn, so a
 * list of thousands stays quick. Rows a server can page belong in DataTable's
 * server mode instead.
 */
export function LocalDataTable<R extends GridValidRowModel>({
  rows, columns, getRowId, searchText, searchPlaceholder, noun, filters = NO_FILTERS, summary, emptyMessage,
  toolbarEnd, pin, onRowClick, selectedId, pageSize = 25, loading, wrap,
}: Props<R>) {
  const [search, setSearch] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [paging, setPaging] = useState({ page: 0, pageSize });

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => (!needle || searchText(r).toLowerCase().includes(needle))
      && filters.every((f) => !values[f.key] || f.keep(r, values[f.key]!)));
  }, [rows, search, values, searchText, filters]);

  // A narrower list starts again at its first page.
  const narrow = (next: () => void) => { next(); setPaging((p) => ({ ...p, page: 0 })); };
  const narrowed = shown.length !== rows.length;

  return (
    <Box>
      {summary && <TotalsStrip items={summary(shown)} />}
      <DataTable
        rows={shown}
        columns={columns}
        getRowId={getRowId}
        loading={loading}
        paginationMode="client"
        paginationModel={paging}
        onPaginationModelChange={setPaging}
        pin={pin}
        emptyMessage={emptyMessage}
        onRowClick={onRowClick ? (p) => onRowClick(p.row) : undefined}
        rowSelectionModel={selectedId ? [selectedId] : []}
        getRowHeight={wrap ? () => 'auto' : undefined}
        sx={[CELL_SX, ...(wrap ? [WRAP_SX] : []), ...(selectedId !== undefined ? [NO_SELECTED_COUNT_SX] : [])]}
        toolbar={{
          search: { value: search, onChange: (v) => narrow(() => setSearch(v)), placeholder: searchPlaceholder },
          filters,
          values,
          onFilterChange: (key, value) => narrow(() => setValues((v) => ({ ...v, [key]: value }))),
          onClearAll: () => narrow(() => { setSearch(''); setValues({}); }),
          end: toolbarEnd,
          count: (
            <Chip size="small" variant="outlined"
                  label={narrowed ? `${shown.length} of ${rows.length}`
                    : `${rows.length} ${rows.length === 1 ? noun[0] : noun[1]}`} />
          ),
        }}
      />
    </Box>
  );
}
